import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, X, Sparkles } from 'lucide-react';

/* ───────────────────────────────────────────────────────────────────────
   V4 Onboarding Tour — spotlight overlay with frosted-glass tooltip card.

   Targets are matched by `data-tour="<id>"` attributes elsewhere in the
   tree. Step 1 (welcome) is centered with no spotlight target.

   Trigger:
   - Auto-opens on first ever load (gated by localStorage 'implify-tour-seen')
   - Re-opens via window.dispatchEvent(new CustomEvent('implify:open-tour'))
   ─────────────────────────────────────────────────────────────────────── */

const TOUR_FLAG_KEY = 'implify-tour-seen';

const STEPS = [
  {
    id: 'welcome',
    target: null,
    title: 'Welcome to Implify',
    body: "Welcome to Implify. This is a demo build to showcase our vision to you and how Implify can drive value to your organization! Let's spend 60 seconds showing you what's interactive.",
    showDontShowAgain: true,
  },
  {
    id: 'sidebar-depts',
    target: 'sidebar-depts',
    placement: 'right',
    title: 'Departments',
    body: 'These are department pages. Each one shows live agent activity, alerts, and decisions waiting on you.',
  },
  {
    id: 'sidebar-system',
    target: 'sidebar-system',
    placement: 'right',
    title: 'Agents, Workflows & Integrations',
    body: 'Browse every AI teammate, run packaged workflows, and connect Implify to the tools you already use.',
  },
  {
    id: 'briefing-cols',
    target: 'briefing-cols',
    placement: 'top',
    title: 'Your morning briefing',
    body: 'Inbox, risks, must-do, and approvals — all in one view. Click Reply, Resolve, Approve, or Begin on any card to dive in.',
  },
  {
    id: 'chat-input',
    target: 'chat-input',
    placement: 'top',
    title: 'Your command center',
    body: 'Type anything, or press A, B, or C on this page to trigger a full multi-agent scenario in real time.',
    highlightKeys: ['A', 'B', 'C'],
  },
  {
    id: 'agent-switcher',
    target: 'agent-switcher',
    placement: 'top',
    title: 'Switch agents anytime',
    body: 'Click here to switch agents. Each persona has their own roster — Luc handles strategy & growth for Arjun, Marsi runs marketing for Priya, Sally drives sales for David. Pick the right teammate for the conversation.',
  },
  {
    id: 'persona-switcher',
    target: 'persona-switcher',
    placement: 'left',
    title: 'Try every persona',
    body: 'Switch between Arjun (CEO), Priya (Marketing Lead), and David (Sales Director) to see the same product from each lens. Cross-account ripples flow between them.',
  },
  {
    id: 'bell',
    target: 'bell',
    placement: 'bottom',
    title: 'Cross-account ripples',
    body: 'Notifications land here when other personas route work to you — approvals, briefings, follow-up tasks.',
  },
  {
    id: 'cheatsheet-btn',
    target: 'cheatsheet-btn',
    placement: 'bottom',
    title: 'All the shortcuts',
    body: 'Press ? anywhere — or click here — to see every keypress shortcut for the page you are on.',
    isFinal: true,
  },
];

/* Public helper: should we auto-open on mount? */
export const shouldAutoOpenTour = () => {
  try { return !localStorage.getItem(TOUR_FLAG_KEY); } catch { return false; }
};

const markTourSeen = () => {
  try { localStorage.setItem(TOUR_FLAG_KEY, '1'); } catch { /* noop */ }
};

const Spotlight = ({ rect, isDark }) => {
  if (!rect) return null;
  const PAD = 8;
  const x = Math.max(0, rect.left - PAD);
  const y = Math.max(0, rect.top - PAD);
  const w = rect.width + PAD * 2;
  const h = rect.height + PAD * 2;
  return (
    <>
      {/* SVG mask — dim everything except the spotlight rect */}
      <svg
        className="fixed inset-0 z-[100] pointer-events-none"
        width="100%"
        height="100%"
        style={{ animation: 'tourFade 0.35s ease-out' }}
      >
        <defs>
          <mask id="tour-spot-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect x={x} y={y} width={w} height={h} rx="14" ry="14" fill="black" />
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill={isDark ? 'rgba(2,8,20,0.78)' : 'rgba(15,23,42,0.55)'}
          mask="url(#tour-spot-mask)"
        />
      </svg>
      {/* Animated glow ring */}
      <div
        className="fixed z-[101] pointer-events-none rounded-2xl"
        style={{
          left: x,
          top: y,
          width: w,
          height: h,
          boxShadow: '0 0 0 2px rgba(129,140,248,0.7), 0 0 32px 6px rgba(129,140,248,0.35)',
          animation: 'tourPulse 2.2s ease-in-out infinite',
          transition: 'left 0.32s cubic-bezier(.4,0,.2,1), top 0.32s cubic-bezier(.4,0,.2,1), width 0.32s, height 0.32s',
        }}
      />
    </>
  );
};

const Tooltip = ({
  step, stepIndex, totalSteps, rect, isDark,
  onNext, onBack, onSkip, dontShowAgain, setDontShowAgain,
}) => {
  const tipRef = useRef(null);
  const [pos, setPos] = useState(null);
  const isCentered = !step.target || !rect;
  const placement = step.placement || 'bottom';

  useLayoutEffect(() => {
    if (isCentered) {
      // Centered modal
      setPos({
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
      });
      return;
    }
    if (!tipRef.current) return;
    const tip = tipRef.current.getBoundingClientRect();
    const W = window.innerWidth;
    const H = window.innerHeight;
    const GAP = 14;
    let left, top;

    const tryPlace = (p) => {
      switch (p) {
        case 'top':
          return { left: rect.left + rect.width / 2 - tip.width / 2, top: rect.top - tip.height - GAP };
        case 'bottom':
          return { left: rect.left + rect.width / 2 - tip.width / 2, top: rect.bottom + GAP };
        case 'left':
          return { left: rect.left - tip.width - GAP, top: rect.top + rect.height / 2 - tip.height / 2 };
        case 'right':
        default:
          return { left: rect.right + GAP, top: rect.top + rect.height / 2 - tip.height / 2 };
      }
    };

    let candidate = tryPlace(placement);
    // Fall back if it overflows
    const overflows = (c) =>
      c.left < 8 || c.top < 8 || c.left + tip.width > W - 8 || c.top + tip.height > H - 8;
    if (overflows(candidate)) {
      const fallbacks = ['bottom', 'top', 'right', 'left'].filter((p) => p !== placement);
      for (const fb of fallbacks) {
        candidate = tryPlace(fb);
        if (!overflows(candidate)) break;
      }
    }
    left = Math.max(12, Math.min(W - tip.width - 12, candidate.left));
    top = Math.max(12, Math.min(H - tip.height - 12, candidate.top));
    setPos({ left, top, transform: 'none' });
  }, [rect, isCentered, placement, stepIndex]);

  const tipBg = isDark
    ? 'bg-[#0f1c2e]/85 border-white/[0.12]'
    : 'bg-white/90 border-black/[0.06]';
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/65' : 'text-slate-600';
  const tHint = isDark ? 'text-white/40' : 'text-slate-400';
  const ringBtn = isDark ? 'border-white/[0.1] text-white/70 hover:bg-white/[0.06]' : 'border-black/[0.08] text-slate-700 hover:bg-black/[0.04]';
  const primaryBtn = 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:from-indigo-400 hover:to-purple-500';

  return (
    <div
      ref={tipRef}
      className={`fixed z-[110] rounded-2xl border backdrop-blur-xl shadow-2xl ${tipBg}`}
      style={{
        ...pos,
        width: 420,
        opacity: pos ? 1 : 0,
        transition: 'opacity 0.25s ease-out, left 0.32s cubic-bezier(.4,0,.2,1), top 0.32s cubic-bezier(.4,0,.2,1)',
        boxShadow: isDark
          ? '0 24px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(129,140,248,0.18)'
          : '0 24px 60px rgba(15,23,42,0.18), 0 0 0 1px rgba(129,140,248,0.18)',
      }}
      data-testid="v4-tour-tooltip"
    >
      {/* Gradient hairline at top */}
      <div className="h-[1.5px] rounded-t-2xl bg-gradient-to-r from-indigo-400/0 via-indigo-400/80 to-purple-500/0" />

      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            {isCentered && <Sparkles className={`w-4 h-4 ${isDark ? 'text-indigo-300' : 'text-indigo-500'}`} />}
            <h3 className={`text-[15px] font-semibold leading-tight ${tp}`}>{step.title}</h3>
          </div>
          <button
            onClick={onSkip}
            className={`p-1 rounded-md transition-colors ${isDark ? 'text-white/40 hover:text-white/70 hover:bg-white/[0.06]' : 'text-slate-400 hover:text-slate-700 hover:bg-black/[0.04]'}`}
            aria-label="Close tour"
            data-testid="v4-tour-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className={`text-[13px] leading-relaxed ${ts}`}>
          {step.highlightKeys
            ? step.body.split(/(\bA\b|\bB\b|\bC\b)/).map((part, i) => {
                if (step.highlightKeys.includes(part)) {
                  return (
                    <kbd
                      key={i}
                      className={`inline-flex items-center justify-center w-6 h-6 mx-0.5 rounded-md text-[11px] font-mono font-semibold ${
                        isDark ? 'bg-indigo-500/20 text-indigo-200 border border-indigo-400/30' : 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                      }`}
                    >
                      {part}
                    </kbd>
                  );
                }
                return <React.Fragment key={i}>{part}</React.Fragment>;
              })
            : step.body}
        </p>

        {step.showDontShowAgain && (
          <label className={`flex items-center gap-2 mt-4 cursor-pointer text-[12px] ${ts}`}>
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-3.5 h-3.5 rounded accent-indigo-500"
              data-testid="v4-tour-dont-show"
            />
            Don't show again
          </label>
        )}

        <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/[0.06] gap-3">
          <span className={`text-[11px] font-mono whitespace-nowrap ${tHint}`}>
            Step {stepIndex + 1} of {totalSteps}
          </span>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={onSkip}
              className={`px-3 py-1.5 rounded-lg text-[12px] font-medium whitespace-nowrap transition-colors ${isDark ? 'text-white/50 hover:text-white/80' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="v4-tour-skip"
            >
              Skip tour
            </button>
            {stepIndex > 0 && (
              <button
                onClick={onBack}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-medium whitespace-nowrap border transition-colors ${ringBtn}`}
                data-testid="v4-tour-back"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </button>
            )}
            <button
              onClick={onNext}
              className={`flex items-center gap-1 px-3.5 py-1.5 rounded-lg text-[12px] font-semibold whitespace-nowrap transition-colors ${primaryBtn}`}
              data-testid="v4-tour-next"
            >
              {step.isFinal ? 'Start exploring' : 'Next'}
              {!step.isFinal && <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const V4OnboardingTour = ({ isDark, open, onClose }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [dontShowAgain, setDontShowAgain] = useState(true);
  const step = STEPS[stepIndex];

  // Recalculate rect when step changes or window resizes
  useLayoutEffect(() => {
    if (!open) return;
    if (!step.target) {
      setRect(null);
      return;
    }
    const calc = () => {
      // Try single element first
      const el = document.querySelector(`[data-tour="${step.target}"]`);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ left: r.left, top: r.top, right: r.right, bottom: r.bottom, width: r.width, height: r.height });
        return;
      }
      // Composite: union the bounding rects of every element in the same tour-group
      const els = document.querySelectorAll(`[data-tour-group="${step.target}"]`);
      if (!els || els.length === 0) {
        setRect(null);
        return;
      }
      let minLeft = Infinity, minTop = Infinity, maxRight = -Infinity, maxBottom = -Infinity;
      els.forEach((node) => {
        const r = node.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) return;
        minLeft = Math.min(minLeft, r.left);
        minTop = Math.min(minTop, r.top);
        maxRight = Math.max(maxRight, r.right);
        maxBottom = Math.max(maxBottom, r.bottom);
      });
      if (!isFinite(minLeft)) {
        setRect(null);
        return;
      }
      setRect({
        left: minLeft, top: minTop, right: maxRight, bottom: maxBottom,
        width: maxRight - minLeft, height: maxBottom - minTop,
      });
    };
    calc();
    window.addEventListener('resize', calc);
    window.addEventListener('scroll', calc, true);
    const iv = setInterval(calc, 250); // catch animated layout shifts
    return () => {
      window.removeEventListener('resize', calc);
      window.removeEventListener('scroll', calc, true);
      clearInterval(iv);
    };
  }, [open, step.target, stepIndex]);

  // Reset to step 0 each time the tour opens
  useEffect(() => { if (open) setStepIndex(0); }, [open]);

  // Notify the rest of the app which step is active so it can pre-open
  // panels (e.g. expand the persona menu when on the persona-switcher step).
  useEffect(() => {
    if (!open) return;
    window.dispatchEvent(new CustomEvent('implify:tour-step-change', { detail: { stepId: step.id } }));
  }, [open, stepIndex, step.id]);

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleSkip();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const finishAndClose = () => {
    if (stepIndex === 0 && dontShowAgain) markTourSeen();
    if (stepIndex > 0) markTourSeen(); // any progress past welcome → mark seen
    window.dispatchEvent(new CustomEvent('implify:tour-step-change', { detail: { stepId: null } }));
    onClose?.();
  };

  const handleNext = () => {
    if (stepIndex < STEPS.length - 1) {
      // If "Don't show again" was checked on welcome, mark seen now too
      if (stepIndex === 0 && dontShowAgain) markTourSeen();
      setStepIndex((i) => i + 1);
    } else {
      markTourSeen();
      // User reached the final step → tour fully completed
      window.dispatchEvent(new CustomEvent('implify:tour-completed'));
      window.dispatchEvent(new CustomEvent('implify:tour-step-change', { detail: { stepId: null } }));
      onClose?.();
    }
  };
  const handleBack = () => setStepIndex((i) => Math.max(0, i - 1));
  const handleSkip = () => { markTourSeen(); finishAndClose(); };

  if (!open) return null;

  return (
    <>
      <style>{`
        @keyframes tourFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes tourPulse {
          0%, 100% { box-shadow: 0 0 0 2px rgba(129,140,248,0.55), 0 0 26px 4px rgba(129,140,248,0.25); }
          50%      { box-shadow: 0 0 0 2px rgba(129,140,248,0.85), 0 0 38px 8px rgba(129,140,248,0.45); }
        }
      `}</style>

      {/* Fallback dim when there's no target (welcome step) */}
      {!step.target && (
        <div
          className="fixed inset-0 z-[100]"
          style={{
            background: isDark ? 'rgba(2,8,20,0.78)' : 'rgba(15,23,42,0.55)',
            animation: 'tourFade 0.35s ease-out',
          }}
        />
      )}

      <Spotlight rect={step.target ? rect : null} isDark={isDark} />

      <Tooltip
        step={step}
        stepIndex={stepIndex}
        totalSteps={STEPS.length}
        rect={step.target ? rect : null}
        isDark={isDark}
        onNext={handleNext}
        onBack={handleBack}
        onSkip={handleSkip}
        dontShowAgain={dontShowAgain}
        setDontShowAgain={setDontShowAgain}
      />
    </>
  );
};

export default V4OnboardingTour;
